import {
  FC,
  CSSProperties,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useSwitchingTogglesArrows from '~/hooks/useSwitchingTogglesArrows';
import { buttonItem, ToggleButtonGroupStyle, useStyles } from './useStyles';

interface ButtonsGroupProps {
  titles: string[];
  selectedButton: string;
  chosenButtonItem?: CSSProperties;
  // eslint-disable-next-line no-unused-vars
  onChange: (type: string) => void;
  customToggleButtonGroupStyle?: CSSProperties;
}

const ButtonsGroup: FC<ButtonsGroupProps> = ({
  titles,
  selectedButton,
  chosenButtonItem,
  onChange,
  customToggleButtonGroupStyle,
}) => {
  const classes = useStyles();
  const [focusedValue, setFocusedValue] = useState(selectedButton);
  const [isChangeWithArrows, setIsChangeWithArrows] = useState(false);
  const buttonsGroupRef = useRef<HTMLDivElement | null>();

  useSwitchingTogglesArrows({
    titles,
    focusedValue,
    onChange,
    setFocusedValue,
    buttonsGroupRef,
    setIsChangeWithArrows,
  });

  useEffect(() => {
    if (!focusedValue) {
      setFocusedValue(selectedButton);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButton]);

  useEffect(() => {
    const toggleButtons = buttonsGroupRef.current?.querySelectorAll(
      '.MuiToggleButton-root',
    );

    if (toggleButtons) {
      const focusedIndex = titles.indexOf(focusedValue);
      const focusedButton = toggleButtons[
        focusedIndex
      ] as HTMLButtonElement | null;

      if (focusedButton && isChangeWithArrows) {
        focusedButton.focus();
      }
    }
  }, [focusedValue, titles, isChangeWithArrows]);

  const handleChange = useCallback(
    (event: any) => {
      onChange((event.target as HTMLInputElement).value);
      setFocusedValue((event.target as HTMLInputElement).value);
    },
    [onChange],
  );

  return (
    <ToggleButtonGroup
      sx={{
        ...classes.root,
        ...ToggleButtonGroupStyle,
        ...customToggleButtonGroupStyle,
      }}
      value={selectedButton}
      exclusive
      role="radiogroup"
      onChange={handleChange}
      ref={buttonsGroupRef}
    >
      {titles.map((title: string) => (
        <ToggleButton
          sx={chosenButtonItem || buttonItem}
          key={title}
          value={title}
          role="radio"
          tabIndex={!focusedValue || title === focusedValue ? 0 : -1}
          selected={title === focusedValue}
        >
          {title}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default ButtonsGroup;
